import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import { moduleReportTableQuery, type ReportModule } from '../../api/settingsModuleReports'
import { LENGTHS, pageButtons } from '../../ui/DataTable'

/**
 * Un tabel al rapoartelor pe module, cu markupul lui `initReportsTable` din
 * `public/js/settings/company_reports.js` - adica DataTables 1.10 cu `dom`-ul
 * de acolo, pus la locul lui:
 *
 *   <"row"<"col-sm-6 dataTableF"f> <"col-sm-6 dataTableA">>
 *   <"row"<"col-sm-12"rt>>
 *   <"row"<"col-sm-2"l><"col-sm-4 aright"i><"col-sm-6"p>>
 *
 * Nu e `ui/DataTable`: acela e tabelul LISTELOR de modul, cu cautarea urcata in
 * antetul paginii si totalul pe intrarea din bara - o singura lista pe ecran.
 * Aici stau patru-cinci tabele unul sub altul, fiecare cu cautarea si
 * paginarea lui in panoul lui, exact cum le desena `dom`-ul de mai sus.
 *
 * Perioada vine de la pagina (`filterFrom`/`filterTo`) si pleaca in fiecare
 * cerere, ca `initReportsTable.data`.
 *
 * SAGETILE inainte/inapoi sunt Font Awesome (`fas fa-chevron-left`), ca in
 * `ui/DataTable`, NU Glyphicons: fontul lui Bootstrap 3 nu ajunge sub `/app`.
 * Cererea pentru `fonts/vendor/bootstrap/dist/glyphicons-halflings-regular.woff2`
 * intoarce `index.html`-ul aplicatiei (3.743 de octeti, `text/html`), fiindca
 * nginx trimite la aplicatie tot ce nu gaseste pe disc si fisierul chiar nu e
 * in `v4/web/public/fonts/vendor/`. `@font-face` pica, si in locul chevronului
 * browserul deseneaza casuta de glif lipsa. Masurat in Edge pe foile de pe
 * server (vezi `settings/softsee/SoftseeListChrome`, care avea markupul copiat
 * de aici): U+E079 da exact aceiasi 50 de pixeli ca un punct de cod pe care
 * nu-l are niciun font, pixel cu pixel - adica nicio sageata.
 */

export interface ReportColumn {
  title: string
  /** Numele cheii din randul intors de server (modelul DataTables vechi). */
  html(row: Record<string, unknown>): string
  /** `sorting_disabled` cand lipseste. */
  orderable?: boolean
}

export function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** `<center>...</center>`, cum scriau modelele vechi celulele numerice. */
export function centered(value: unknown): string {
  return '<center>' + esc(value) + '</center>'
}

export function ReportTable({ module, table, id, columns, order, from, to, severity }: {
  module: ReportModule
  table: string
  /** `id`-ul tabelului din macheta veche (`newDevices`, `costs`...). */
  id: string
  columns: ReportColumn[]
  /** Ordinea de pornire din macheta (`[[0, 'desc']]`). */
  order: { column: number; dir: 'asc' | 'desc' }
  from: string
  to: string
  severity?: number
}) {
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(25)
  const [sort, setSort] = useState(order)
  const [draft, setDraft] = useState('')
  const [search, setSearch] = useState('')

  /* Cautarea pleaca la 400 ms dupa ultima tasta, ca in DataTables. */
  useEffect(() => {
    if (draft === search) return
    const t = window.setTimeout(() => {
      setSearch(draft)
      setStart(0)
    }, 400)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  /* O perioada noua reia lista de la prima pagina, ca `destroy: true` in portal. */
  useEffect(() => {
    setStart(0)
  }, [from, to])

  const page = useQuery({
    ...moduleReportTableQuery(module, table, {
      draw: 1,
      start,
      length,
      orderColumn: sort.column,
      orderDir: sort.dir,
      search,
      filterFrom: from,
      filterTo: to,
      severity,
    }),
    placeholderData: keepPreviousData,
  })

  const rows = page.data?.data ?? []
  const total = page.data?.recordsFiltered ?? 0
  const pages = Math.max(1, Math.ceil(total / length))
  const current = Math.min(Math.floor(start / length), pages - 1)
  const goTo = (p: number) => setStart(Math.max(0, Math.min(pages - 1, p)) * length)

  const headerClass = (c: ReportColumn, i: number) => {
    if (c.orderable === false) return 'sorting_disabled'
    if (sort.column === i) return sort.dir === 'asc' ? 'sorting_asc' : 'sorting_desc'
    return 'sorting'
  }

  const failure = page.error instanceof ApiError ? page.error.message : page.error ? 'This did not load.' : ''

  return (
    <div id={`${id}_wrapper`} className="dataTables_wrapper form-inline dt-bootstrap no-footer">
      <div className="row">
        <div className="col-sm-6 dataTableF">
          <div id={`${id}_filter`} className="dataTables_filter input-icon">
            <label>
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="search"
                className="form-control input-sm"
                placeholder="Search..."
                aria-controls={id}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            </label>
          </div>
        </div>
        <div className="col-sm-6 dataTableA"></div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div
            className="dataTables_processing panel panel-default"
            id={`${id}_processing`}
            style={{ display: page.isFetching ? 'block' : 'none' }}
          >
            <div className="spinnerballs default">
              <div className="bounce1"></div>
              <div className="bounce2"></div>
              <div className="bounce3"></div>
            </div>
          </div>
          <table
            className="table table-striped table-hover dataTable no-footer"
            width="100%"
            id={id}
            role="grid"
            aria-describedby={`${id}_info`}
          >
            <thead>
              <tr role="row">
                {columns.map((c, i) => (
                  <th
                    key={c.title}
                    className={headerClass(c, i)}
                    tabIndex={0}
                    aria-controls={id}
                    onClick={() => {
                      if (c.orderable === false) return
                      setSort({ column: i, dir: sort.column === i && sort.dir === 'asc' ? 'desc' : 'asc' })
                      setStart(0)
                    }}
                  >
                    {c.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {failure !== '' ? (
                <tr className="odd">
                  <td valign="top" colSpan={columns.length} className="dataTables_empty">
                    {failure}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="odd">
                  <td valign="top" colSpan={columns.length} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={String(row.DT_RowId ?? i)} id={String(row.DT_RowId ?? '')} className={i % 2 === 0 ? 'odd' : 'even'} role="row">
                    {columns.map((c) => (
                      <td key={c.title} dangerouslySetInnerHTML={{ __html: c.html(row) }} />
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${id}_length`}>
            <label>
              <select
                name={`${id}_length`}
                aria-controls={id}
                className="form-control input-sm"
                value={length}
                onChange={(e) => {
                  setLength(Number(e.target.value))
                  setStart(0)
                }}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${id}_info`} role="status" aria-live="polite">
            {total === 0
              ? 'Showing 0 to 0 of 0 entries'
              : `Showing ${start + 1} to ${Math.min(start + length, total)} of ${total} entries`}
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${id}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${current === 0 ? ' disabled' : ''}`} id={`${id}_previous`}>
                <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => (e.preventDefault(), goTo(current - 1))}>
                  <i className="fas fa-chevron-left" aria-hidden="true"></i>
                </a>
              </li>
              {pageButtons(current, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li className="paginate_button disabled" key={`e${i}`}>
                    <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li className={`paginate_button${b === current ? ' active' : ''}`} key={b}>
                    <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => (e.preventDefault(), goTo(b))}>
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${current >= pages - 1 ? ' disabled' : ''}`} id={`${id}_next`}>
                <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => (e.preventDefault(), goTo(current + 1))}>
                  <i className="fas fa-chevron-right" aria-hidden="true"></i>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}
